/**
 * Security Audit Detail Page — F010
 *
 * Displays audit header + items grouped by category (collapsible).
 * Per-item: compliance status, risk level, evidence, remediation.
 * Edit items via AuditItemModal.
 *
 * Key divergences from spec (PATTERN-DB-007):
 *  - No sign-off endpoint (schema lacks signedOffBy/signedOffAt) — sign-off omitted
 *  - complianceStatus: compliant | non_compliant | partially_compliant | not_applicable (NO not_assessed)
 *  - riskLevel: critical | high | medium | low | informational
 *  - evidence: plain text (NOT evidenceLink)
 *  - auditStatus: planned | in_progress | completed | cancelled
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Pencil,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ComplianceLayout } from "./compliance-layout";
import { AuditItemModal, type AuditItemData } from "@/components/modals/audit-item-modal";
import { format } from "date-fns";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuditSummary {
  totalItems: number;
  compliant: number;
  nonCompliant: number;
  partiallyCompliant: number;
  notApplicable: number;
  overdueRemediations: number;
}

interface SecurityAuditDetail {
  id: string;
  auditType: string;
  auditName: string;
  auditDescription: string | null;
  auditStatus: string;
  plannedDate: string | null;
  startDate: string | null;
  completionDate: string | null;
  auditorName: string | null;
  auditorOrganisation: string | null;
  executiveSummary: string | null;
  reportUrl: string | null;
  nextAuditDate: string | null;
  createdAt: string;
  items: AuditItemData[];
  summary: AuditSummary;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AUDIT_TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  internal: { label: "Internal", className: "bg-blue-100 text-blue-700 border-blue-300" },
  external: { label: "External", className: "bg-purple-100 text-purple-700 border-purple-300" },
  regulatory: { label: "Regulatory", className: "bg-amber-100 text-amber-700 border-amber-300" },
  certification: { label: "Certification", className: "bg-green-100 text-green-700 border-green-300" },
};

const AUDIT_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  planned: { label: "Planned", className: "bg-gray-100 text-gray-700 border-gray-300" },
  in_progress: { label: "In Progress", className: "bg-blue-100 text-blue-700 border-blue-300" },
  completed: { label: "Completed", className: "bg-green-100 text-green-700 border-green-300" },
  cancelled: { label: "Cancelled", className: "bg-red-100 text-red-700 border-red-300" },
};

const COMPLIANCE_STATUS_CONFIG: Record<
  string,
  { label: string; badgeClass: string; rowClass?: string }
> = {
  compliant: {
    label: "Compliant",
    badgeClass: "bg-green-100 text-green-700 border-green-300",
    rowClass: "",
  },
  non_compliant: {
    label: "Non-Compliant",
    badgeClass: "bg-red-100 text-red-700 border-red-300",
    rowClass: "bg-red-50/40",
  },
  partially_compliant: {
    label: "Partial",
    badgeClass: "bg-amber-100 text-amber-700 border-amber-300",
    rowClass: "bg-amber-50/40",
  },
  not_applicable: {
    label: "N/A",
    badgeClass: "bg-gray-100 text-gray-600 border-gray-300",
    rowClass: "",
  },
};

const RISK_LEVEL_CONFIG: Record<string, { label: string; className: string }> = {
  critical: { label: "Critical", className: "bg-red-100 text-red-800 border-red-400" },
  high: { label: "High", className: "bg-orange-100 text-orange-700 border-orange-300" },
  medium: { label: "Medium", className: "bg-amber-100 text-amber-700 border-amber-300" },
  low: { label: "Low", className: "bg-green-100 text-green-700 border-green-300" },
  informational: { label: "Info", className: "bg-blue-100 text-blue-600 border-blue-300" },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

function compliancePct(summary: AuditSummary): number {
  const assessed = summary.totalItems - summary.notApplicable;
  if (assessed <= 0) return 0;
  return Math.round((summary.compliant / assessed) * 100);
}

function pctColour(pct: number): string {
  if (pct >= 80) return "text-green-700";
  if (pct >= 50) return "text-amber-700";
  return "text-red-700";
}

// ---------------------------------------------------------------------------
// Category Group
// ---------------------------------------------------------------------------

interface CategoryGroupProps {
  category: string;
  items: AuditItemData[];
  onEditItem: (item: AuditItemData) => void;
}

function CategoryGroup({ category, items, onEditItem }: CategoryGroupProps) {
  const [open, setOpen] = useState(true);

  const compliantCount = items.filter((i) => i.complianceStatus === "compliant").length;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-muted/50 transition-colors"
          data-testid={`category-group-${category.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <span className="font-medium text-sm">{category}</span>
          <span className="ml-auto text-xs text-muted-foreground">
            {compliantCount}/{items.length} compliant
          </span>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="w-24">Code</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-36">Status</TableHead>
              <TableHead className="w-28">Risk</TableHead>
              <TableHead className="w-24">Remediation</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const statusCfg =
                COMPLIANCE_STATUS_CONFIG[item.complianceStatus] ??
                COMPLIANCE_STATUS_CONFIG.not_applicable;
              const riskCfg = item.riskLevel
                ? (RISK_LEVEL_CONFIG[item.riskLevel] ?? null)
                : null;

              return (
                <TableRow
                  key={item.id}
                  className={cn("hover:bg-muted/30", statusCfg.rowClass)}
                  data-testid={`row-audit-item-${item.id}`}
                >
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {item.itemCode ?? "-"}
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{item.itemDescription}</div>
                    {item.evidence && (
                      <div className="mt-0.5 text-xs text-muted-foreground truncate max-w-xs">
                        Evidence: {item.evidence}
                      </div>
                    )}
                    {item.remediationRequired && item.remediationDueDate && (
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-amber-600">
                        <AlertTriangle className="h-3 w-3" />
                        Remediation due {formatDate(item.remediationDueDate)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={statusCfg.badgeClass}
                      data-testid={`status-badge-item-${item.id}`}
                    >
                      {statusCfg.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {riskCfg ? (
                      <Badge
                        variant="outline"
                        className={riskCfg.className}
                        data-testid={`risk-badge-item-${item.id}`}
                      >
                        {riskCfg.label}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.remediationRequired ? (
                      <span className="text-xs font-medium text-amber-700">Required</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      data-testid={`button-edit-item-${item.id}`}
                      onClick={() => onEditItem(item)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// Main content
// ---------------------------------------------------------------------------

function SecurityAuditDetailContent({ auditId }: { auditId: string }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [editingItem, setEditingItem] = useState<AuditItemData | null>(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);

  const { data: audit, isLoading, isError } = useQuery<SecurityAuditDetail>({
    queryKey: [`/api/compliance/security-audits/${auditId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/compliance/security-audits/${auditId}`);
      if (!response.ok) throw new Error("Failed to load audit");
      return response.json();
    },
  });

  // Update audit status mutation
  const statusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/security-audits/${auditId}`,
        { auditStatus: newStatus }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Failed to update status");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/security-audits/${auditId}`],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/security-audits"] });
      toast({ title: "Audit status updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleEditItem = (item: AuditItemData) => {
    setEditingItem(item);
    setIsItemModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-4 w-full animate-pulse rounded bg-muted" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || !audit) {
    return (
      <div className="mx-auto max-w-5xl">
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">Audit not found or failed to load.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setLocation("/compliance/security-audits")}
            >
              Back to Audits
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const typeCfg =
    AUDIT_TYPE_CONFIG[audit.auditType] ?? {
      label: audit.auditType,
      className: "bg-gray-100 text-gray-700 border-gray-300",
    };
  const statusCfg =
    AUDIT_STATUS_CONFIG[audit.auditStatus] ?? {
      label: audit.auditStatus,
      className: "bg-gray-100 text-gray-700 border-gray-300",
    };

  const pct = compliancePct(audit.summary);

  // Group items by category
  const categoryMap = new Map<string, AuditItemData[]>();
  for (const item of audit.items) {
    const cat = item.itemCategory;
    if (!categoryMap.has(cat)) {
      categoryMap.set(cat, []);
    }
    categoryMap.get(cat)!.push(item);
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      {/* Back link */}
      <Button
        variant="ghost"
        size="sm"
        className="gap-1 text-muted-foreground"
        onClick={() => setLocation("/compliance/security-audits")}
        data-testid="button-back-to-audits"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Audits
      </Button>

      {/* Audit Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1
                  className="text-xl font-semibold"
                  data-testid="audit-detail-name"
                >
                  {audit.auditName}
                </h1>
                <Badge variant="outline" className={typeCfg.className}>
                  {typeCfg.label}
                </Badge>
                <Badge
                  variant="outline"
                  className={statusCfg.className}
                  data-testid="audit-detail-status-badge"
                >
                  {statusCfg.label}
                </Badge>
              </div>

              {audit.auditDescription && (
                <p className="text-sm text-muted-foreground">{audit.auditDescription}</p>
              )}

              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                {audit.plannedDate && (
                  <span>Planned: {formatDate(audit.plannedDate)}</span>
                )}
                {audit.startDate && (
                  <span>Started: {formatDate(audit.startDate)}</span>
                )}
                {audit.completionDate && (
                  <span>Completed: {formatDate(audit.completionDate)}</span>
                )}
                {audit.auditorName && (
                  <span>
                    Auditor: {audit.auditorName}
                    {audit.auditorOrganisation
                      ? ` (${audit.auditorOrganisation})`
                      : ""}
                  </span>
                )}
              </div>
            </div>

            {/* Compliance summary */}
            <div
              className="rounded-lg border bg-muted/30 px-5 py-3 text-center shrink-0"
              data-testid="audit-compliance-summary"
            >
              <div className={cn("text-3xl font-bold", pctColour(pct))}>{pct}%</div>
              <div className="text-xs text-muted-foreground mt-0.5">Compliance</div>
              <div className="text-xs text-muted-foreground">
                {audit.summary.compliant}/{audit.summary.totalItems} items compliant
              </div>
              {audit.summary.overdueRemediations > 0 && (
                <div className="mt-1 flex items-center justify-center gap-1 text-xs text-red-600">
                  <AlertTriangle className="h-3 w-3" />
                  {audit.summary.overdueRemediations} overdue
                </div>
              )}
            </div>
          </div>

          {/* Status actions */}
          {audit.auditStatus === "planned" && (
            <div className="mt-4 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="button-mark-in-progress"
                onClick={() => statusMutation.mutate("in_progress")}
                disabled={statusMutation.isPending}
              >
                Mark In Progress
              </Button>
            </div>
          )}
          {audit.auditStatus === "in_progress" && (
            <div className="mt-4 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="button-mark-completed"
                onClick={() => statusMutation.mutate("completed")}
                disabled={statusMutation.isPending}
              >
                Mark Completed
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit Items */}
      <Card>
        <div className="border-b px-4 py-3">
          <h2 className="font-semibold">Audit Items</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {audit.items.length} items across {categoryMap.size} categories
          </p>
        </div>

        {audit.items.length === 0 ? (
          <CardContent className="py-12 text-center text-muted-foreground">
            <p>No audit items found.</p>
            <p className="text-xs mt-1">
              Items are auto-populated when the audit is created from a template.
            </p>
          </CardContent>
        ) : (
          <div className="divide-y">
            {Array.from(categoryMap.entries()).map(([category, items]) => (
              <CategoryGroup
                key={category}
                category={category}
                items={items}
                onEditItem={handleEditItem}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Item edit modal */}
      <AuditItemModal
        open={isItemModalOpen}
        onOpenChange={setIsItemModalOpen}
        item={editingItem}
        auditId={auditId}
        onSuccess={() => setEditingItem(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SecurityAuditDetailPage() {
  const [, params] = useRoute("/compliance/security-audits/:id");
  const auditId = params?.id ?? "";

  return (
    <ComplianceLayout>
      <SecurityAuditDetailContent auditId={auditId} />
    </ComplianceLayout>
  );
}
